/** Tab 2: Address */
import { useState } from 'react'
import { Button, Card, Form, Input, message, Space, Switch, Row, Col, Select } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { joiningApi, type JoiningDetail } from '@/features/hr/joiningApi'
import { INDIAN_STATES } from '@/shared/types/hr'

const STATE_OPTIONS = INDIAN_STATES.map((s) => ({ label: s, value: s }))

interface Props { detail: JoiningDetail }

export default function AddressTab({ detail }: Props) {
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [dirty, setDirty] = useState(false)

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => joiningApi.update(detail.id, values),
    onSuccess: () => {
      message.success('Saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const initial: Record<string, unknown> = { ...detail.employee }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initial}
      onValuesChange={() => setDirty(true)}
      onFinish={(values) => {
        const out: Record<string, unknown> = {}
        Object.keys(values).forEach((k) => {
          const v = values[k]
          out[k] = dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : v
        })
        save.mutate(out)
      }}
    >
      <Card size="small" title="Current Address" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="current_address_line1" label="Address Line 1">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="current_address_line2" label="Address Line 2">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="village" label="Village">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="current_taluka" label="Taluka">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="current_district" label="District">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="current_state" label="State">
              <Select
                showSearch
                allowClear
                placeholder="Pick state"
                options={STATE_OPTIONS}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="pincode" label="Pincode">
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Card size="small" title="Permanent Address" style={{ marginBottom: 12 }}>
        <Form.Item name="permanent_address_same" label="Same as Current" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="perm_address_line1" label="Address Line 1">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="perm_address_line2" label="Address Line 2">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="perm_taluka" label="Taluka">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="perm_district" label="District">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="perm_state" label="State">
              <Select
                showSearch
                allowClear
                placeholder="Pick state"
                options={STATE_OPTIONS}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="perm_pincode" label="Pincode">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="native_place" label="Native Place">
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Card size="small" title="Facilities" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="transport_required" label="Transport Required" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="hostel_or_quarter_required" label="Hostel/Quarter Required" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Space>
        <Button type="primary" htmlType="submit" disabled={!dirty} loading={save.isPending}>
          Save
        </Button>
        {dirty && <span style={{ color: '#faad14' }}>Unsaved changes</span>}
      </Space>
    </Form>
  )
}
